import {Component, inject, OnInit, OnDestroy, signal} from '@angular/core';
import {AsyncPipe} from "@angular/common";
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {MatIcon} from "@angular/material/icon";
import {MatListItem, MatListItemIcon, MatNavList} from "@angular/material/list";
import {RouterLink} from "@angular/router";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {AppConfigService} from "../../../services/app-config.service";
import {SupportRequestService} from "../../../services/support-request.service";
import {MatBadge} from "@angular/material/badge";
import {
  UserHasCausableAdminAccessDirective
} from "../../../directives/permissions/user-has-causable-admin-access.directive";
import {ActiveUserService} from "../../../services/active-user.service";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatTooltip} from "@angular/material/tooltip";
import {OidcService} from "../../../services/oidc.service";
import {PermissionsService} from "../../../services/permissions.service";
import {MatLine} from "@angular/material/core";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-cross-game-admin-side-nav',
  standalone: true,
  imports: [
    AsyncPipe,
    HasPermissionDirective,
    MatIcon,
    MatListItem,
    MatListItemIcon,
    MatNavList,
    RouterLink,
    MatBadge,
    UserHasCausableAdminAccessDirective,
    MatExpansionModule,
    MatTooltip,
    MatLine
  ],
  templateUrl: './cross-game-admin-side-nav.component.html',
  styleUrl: '../side-nav.component.scss'
})
export class CrossGameAdminSideNav implements OnInit, OnDestroy {
  private oidcService = inject(OidcService);
  private appConfigService = inject(AppConfigService);
  private activeUserService = inject(ActiveUserService);
  private permissionService = inject(PermissionsService);
  private supportRequestService = inject(SupportRequestService);

  private destroy$ = new Subject<void>();

  public permissionTypes = PermissionTypes;
  public currentEnvironment: string = "";
  public hangfireDashboardLink: string = "";

  // Signal for assigned support requests count
  public assignedSupportRequestsCount = signal<number>(0);

  ngOnInit(): void {
    this.appConfigService.appConfig$.subscribe(config => {
      this.currentEnvironment = config.currentEnvironment;
      this.hangfireDashboardLink = config.commonUrl + '/hangfire';
    });

    // Initial fetch
    this.refreshAssignedRequestsCount();

    // Listen for assignment changes (assign, resolve, escalate)
    this.supportRequestService.assignmentChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshAssignedRequestsCount();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshAssignedRequestsCount(): void {
    const userId = this.activeUserService.activeUser()?.Id;

    if (!userId) return;

    // Only fetch if user has permission to view support requests
    if (!this.permissionService.userHasPermission(PermissionTypes.ViewSupportRequests)) {
      return;
    }

    // Cross-game admin fetches all assigned requests (no gameId filter)
    this.supportRequestService.fetchMyAssignedRequests(userId, null).subscribe({
      next: (requests) => {
        // Only count unresolved tickets
        const unresolvedCount = requests.filter(r => !r.Resolved).length;
        this.assignedSupportRequestsCount.set(unresolvedCount);
      },
      error: () => {
        // Silently fail - don't show error for background refresh
      }
    });
  }

  onLogoutClick(): void {
    this.oidcService.logout();
  }
}
